import React from 'react';
import Catalog from '~/Catalog';
import Button from '~/components/atom/Button';
import {
  useUpdateLfaAccountSettingsMutation,
  Lfa_Account_Status,
} from '~/graphql/types';
import useAddToast from '~/hooks/useAddToast';
import useConfirmModal from '~/hooks/useConfirmModal';
import useCurrentAccount from '~/hooks/useCurrentAccount';
import formatToastMessage from '~/util/formatToastMessage';
import ActivateButton from './components/ActivateButton';

const text = {
  enable: {
    title: 'Inschakelen',
    message: 'Weet je het zeker dat je wilt inschakelen?',
  },
  disable: {
    title: 'Uitschakelen',
    message: 'Weet je het zeker dat je wilt uitschakelen?',
  },
  updateError: Catalog.genericUnknownErrorMessageShort,
  updateSuccess: Catalog.genericSuccessMessage,
  waitingOnManualAction: 'Wacht op handmatige actie',
};

type Props = {
  status: Lfa_Account_Status;
  readyToActivate: boolean;
  callbackPhoneNumber: string | null;
  refetch: () => Promise<any>;
};

const StatusUpdate: React.FCC<Props> = ({
  status,
  readyToActivate,
  callbackPhoneNumber,
  refetch,
}) => {
  const addToast = useAddToast();
  const { id: accountId } = useCurrentAccount();

  const [updateLfaAccountSettings, { loading: updateLoading }] =
    useUpdateLfaAccountSettingsMutation();

  const isEnabled = status === Lfa_Account_Status.Active;

  const { modal, setShowModal } = useConfirmModal({
    level: isEnabled ? 'danger' : 'success',
    labels: {
      title: isEnabled ? text.disable.title : text.enable.title,
      message: isEnabled ? text.disable.message : text.enable.message,
    },
    buttons: [
      {
        label: Catalog.confirm,
        onClick: async () => {
          void updateLfaAccountSettings({
            variables: {
              accountId,
              update: isEnabled
                ? { RequestDisablement: true }
                : { RequestEnablement: true },
            },
          }).then(({ data, errors }) => {
            if (errors && errors.length > 0) {
              return addToast([formatToastMessage(text.updateError, 'danger')]);
            }

            if (data) {
              addToast([formatToastMessage(text.updateSuccess, 'success')]);
              void refetch();
            }
          });
        },
      },
    ],
  });

  return (
    <>
      {modal}

      <ButtonToRender
        status={status}
        enableActivation={readyToActivate}
        updateLoading={updateLoading}
        setShowModal={setShowModal}
        refetch={refetch}
        callbackPhoneNumber={callbackPhoneNumber}
      />
    </>
  );
};

const ButtonToRender: React.FC<{
  status: Lfa_Account_Status;
  enableActivation: boolean;
  updateLoading: boolean;
  callbackPhoneNumber: string | null;
  setShowModal: (show: boolean) => void;
  refetch: () => Promise<any>;
}> = ({
  status,
  enableActivation,
  updateLoading,
  setShowModal,
  refetch,
  callbackPhoneNumber,
}) => {
  if (enableActivation) {
    return (
      <ActivateButton
        refetch={refetch}
        callbackPhoneNumber={callbackPhoneNumber}
      />
    );
  }

  if (status === Lfa_Account_Status.Active) {
    return (
      <Button
        label={text.disable.title}
        onClick={() => setShowModal(true)}
        size="medium"
        loading={updateLoading}
        appearance="danger"
      />
    );
  }

  if (status === Lfa_Account_Status.Inactive) {
    return (
      <Button
        label={text.enable.title}
        onClick={() => setShowModal(true)}
        size="medium"
        loading={updateLoading}
        appearance="secondary"
      />
    );
  }

  return null;
};

export default StatusUpdate;
